import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const HEADLINE_WORDS = ["Your", "Business", "Is", "Not", "Slow.", "Your", "Systems", "Are."];

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-24 pb-16">
      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => {
          const size = 2 + ((i * 7) % 5);
          const left = (i * 37) % 100;
          const top = (i * 53) % 100;
          const delay = (i * 0.3) % 6;
          return (
            <span
              key={`hero-particle-${i}`}
              className="particle"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                left: `${left}%`,
                top: `${top}%`,
                animation: `float-y ${6 + (i % 4)}s ease-in-out ${delay}s infinite`,
                opacity: 0.5 + ((i % 5) / 10),
              }}
            />
          );
        })}
      </div>

      {/* Wave gradient overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-[radial-gradient(closest-side,rgba(0,240,255,0.18),transparent_70%)] blur-2xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(139,92,246,0.18),transparent_70%)] blur-2xl" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[radial-gradient(closest-side,rgba(0,122,255,0.18),transparent_70%)] blur-2xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs uppercase tracking-[0.18em] text-white/70 mb-8"
          data-testid="hero-badge"
        >
          <Sparkles size={14} className="text-[#00F0FF]" />
          AI Automation for Local Businesses
        </motion.div>

        <h1
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] text-white"
          data-testid="hero-headline"
        >
          {HEADLINE_WORDS.map((word, i) => {
            const highlight = word.includes("Not") || word.includes("Systems");
            return (
              <motion.span
                key={`${i}-${word}`}
                initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                className={`inline-block mr-3 ${highlight ? "bg-gradient-to-r from-[#00F0FF] via-[#007AFF] to-[#8B5CF6] bg-clip-text text-transparent" : ""}`}
              >
                {word}
              </motion.span>
            );
          })}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="mt-8 text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subheadline"
        >
          We build AI systems that can help you <span className="text-white font-semibold">3X your lead response speed</span>, close more clients, and scale without hiring.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.2 }}
          className="mt-10 flex flex-col items-center gap-4"
        >
          <button
            onClick={scrollToContact}
            className="btn-primary inline-flex items-center gap-2 text-base"
            data-testid="hero-cta"
          >
            Get Your Automation Setup
            <ArrowRight size={18} />
          </button>
          <p className="text-sm text-white/50" data-testid="hero-highlight">
            Turn your business into a <span className="text-[#00F0FF]">3X growth system</span>.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-16 flex flex-wrap items-center justify-center gap-6 text-xs uppercase tracking-[0.2em] text-white/40"
        >
          <span>· Gyms</span>
          <span>· Consultants</span>
          <span>· Education</span>
          <span>· Small Businesses</span>
        </motion.div>
      </div>
    </section>
  );
}
